import { VideoAsset, VideoViewType } from '$Utilities/models/videoViews';
import { useMemo } from 'react';

interface VideoLayout {
    mainVideos: VideoAsset[];
    secondaryVideos: VideoAsset[];
}

const useVideoViewLayout = (videoAssets: VideoAsset[], primaryChannelNumber: number, viewMode: VideoViewType): VideoLayout => {
    return useMemo(() => {
        if (viewMode === 'Single') {
            const mainVideos = videoAssets.filter((a) => a.channelNumber === primaryChannelNumber);
            const secondaryVideos = videoAssets.filter((a) => a.channelNumber !== primaryChannelNumber);
            return { mainVideos, secondaryVideos };
        }

        if (viewMode === 'Double') {
            const getOppositeChannel = (primary: number): number | undefined => {
                if (primary === 1) return videoAssets.find((a) => a.channelNumber === 2)?.channelNumber;
                if (primary === 2) return videoAssets.find((a) => a.channelNumber === 1)?.channelNumber;
                return videoAssets.find((a) => a.channelNumber !== primary)?.channelNumber;
            };

            const oppositeChannel = getOppositeChannel(primaryChannelNumber);
            const mainVideos = videoAssets.filter((a) => a.channelNumber === primaryChannelNumber || a.channelNumber === oppositeChannel);

            if (mainVideos.length === 1) {
                const other = videoAssets.find((a) => a.channelNumber !== primaryChannelNumber);
                if (other) mainVideos.push(other);
            }

            const secondaryVideos = videoAssets.filter((a) => !mainVideos.includes(a));
            return { mainVideos, secondaryVideos };
        }

        if (viewMode === 'Grid') {
            // Left column: primary channel + odd-indexed remaining channels
            // Right column: channel 1 or 2 (whichever isn't primary) + even-indexed remaining channels
            // Sort all by channel number for deterministic ordering
            const sorted = [...videoAssets].sort((a, b) => (a.channelNumber ?? 0) - (b.channelNumber ?? 0));
            const primary = sorted.filter((a) => a.channelNumber === primaryChannelNumber);
            const rest = sorted.filter((a) => a.channelNumber !== primaryChannelNumber);

            const leftColumn = [...primary];
            const rightColumn: VideoAsset[] = [];

            rest.forEach((video, index) => {
                if (index % 2 === 0) rightColumn.push(video);
                else leftColumn.push(video);
            });

            return { mainVideos: leftColumn, secondaryVideos: rightColumn };
        }

        return {
            mainVideos: [],
            secondaryVideos: [],
        };
    }, [videoAssets, primaryChannelNumber, viewMode]);
};

export default useVideoViewLayout;
